import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BtcDominanceDetail = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  // ...existing code...
  const [analysis, setAnalysis] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch current BTC dominance
        const currentRes = await axios.get('/api/market/btc-dominance/latest');
        setData(currentRes.data);

        // Fetch long history (1 year)
        const historyRes = await axios.get('/api/market/btc-dominance/timeframe/1y');
        setHistory(historyRes.data.data || []);

        // Fetch portfolio for impact analysis
        const portfolioData = localStorage.getItem('portfolio');
        if (portfolioData) {
          setPortfolio(JSON.parse(portfolioData));
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching BTC Dominance detail:', err);
        setError('Unable to fetch BTC Dominance data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Generate auto-analysis based on trend detection
  useEffect(() => {
    if (history.length > 0) {
      const analysisResult = generateAnalysis(history, data);
      setAnalysis(analysisResult);
    }
  }, [history, data]);

  // Calculate portfolio altcoin exposure
  const calculateAltcoinExposure = () => {
    if (!portfolio || !portfolio.investments || portfolio.investments.length === 0) {
      return { percentage: 0, count: 0, totalValue: 0 };
    }

    let altcoinValue = 0;
    let totalValue = 0;

    portfolio.investments.forEach(inv => {
      const currentValue = inv.currentAmountEur || 0;
      totalValue += currentValue;
      
      // Exclude Bitcoin
      if (inv.cryptoSymbol && inv.cryptoSymbol.toUpperCase() !== 'BTC') {
        altcoinValue += currentValue;
      }
    });

    const percentage = totalValue > 0 ? Math.round((altcoinValue / totalValue) * 100) : 0;
    return {
      percentage,
      count: portfolio.investments.filter(i => i.cryptoSymbol?.toUpperCase() !== 'BTC').length,
      totalValue: altcoinValue
    };
  };

  const generateAnalysis = (historyData, currentData) => {
    if (historyData.length < 2) return null;

    // Sort by timestamp
    const sorted = [...historyData].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    
    const current = currentData?.dominance || 0;
    const oneWeekAgo = sorted[Math.max(0, sorted.length - 7)]?.dominance || current;
    const oneMonthAgo = sorted[Math.max(0, sorted.length - 30)]?.dominance || current;
    const threeMonthsAgo = sorted[Math.max(0, sorted.length - 90)]?.dominance || current;

    const change1w = current - oneWeekAgo;
    const change1m = current - oneMonthAgo;
    const change3m = current - threeMonthsAgo;

    // Determine trend
    let trend = 'STABLE';
    let trendIcon = '➡️';
    
    if (change1w > 3) {
      trend = 'BULLISH';
      trendIcon = '📈';
    } else if (change1w < -3) {
      trend = 'BEARISH';
      trendIcon = '📉';
    }

    // Generate analysis text
    let analysisText = '';
    let riskLevel = 'MODÉRÉ';

    if (change1w > 3) {
      analysisText = `🎯 BTC dominance a augmenté de ${change1w.toFixed(1)}% la semaine dernière.
      
Cela indique une rotation vers les actifs défensifs. Les investisseurs favorisent Bitcoin par rapport aux altcoins, souvent en réponse aux conditions de marché volatiles ou aux ajustements de portefeuille strategiques.

📊 Contexte historique : Cette corrélation inverse entre la dominance BTC et la performance des altcoins est bien documentée. Lorsque BTC augmente, les altcoins sous-performent généralement.`;
      riskLevel = 'ÉLEVÉ';
    } else if (change1w < -3) {
      analysisText = `🎯 BTC dominance a diminué de ${Math.abs(change1w).toFixed(1)}% la semaine dernière.
      
Cela indique une "alt season" potentielle. Les investisseurs diversifient vers les altcoins, signalant une appétence au risque accrue sur le marché.

📊 Contexte historique : Les périodes de baisse de dominance BTC s'accompagnent généralement d'une surperformance des altcoins, particulièrement dans les phases de bull market.`;
      riskLevel = 'MODÉRÉ';
    } else {
      analysisText = `🎯 BTC dominance est stable autour de ${current.toFixed(1)}%.
      
Le marché maintient un équilibre entre Bitcoin et les altcoins. Cet équilibre suggère une certaine stabilité du sentiment du marché.

📊 Contexte historique : Les périodes de stabilité reflètent généralement un consensus du marché sur la valorisation relative de Bitcoin.`;
      riskLevel = 'MODÉRÉ';
    }

    if (change3m > 0) {
      analysisText += `\n\n⏳ Tendance long terme : Sur 3 mois, la dominance augmente (+${change3m.toFixed(1)}%) - mouvement défensif confirmé.`;
    } else if (change3m < 0) {
      analysisText += `\n\n⏳ Tendance long terme : Sur 3 mois, la dominance diminue (${change3m.toFixed(1)}%) - alt season confirmée.`;
    }

    return {
      text: analysisText,
      trend,
      trendIcon,
      change1w,
      change1m,
      change3m,
      riskLevel
    };
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '1.1rem', color: '#666' }}>Chargement des données...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '1.1rem', color: '#e74c3c' }}>{error}</div>
        <button
          onClick={() => navigate(-1)}
          style={{
            marginTop: '1rem',
            padding: '10px 20px',
            borderRadius: 8,
            background: '#1976d2',
            color: '#fff',
            border: 'none',
            cursor: 'pointer'
          }}
        >
          Retour
        </button>
      </div>
    );
  }

  const altcoinExposure = calculateAltcoinExposure();
  const analysisData = analysis || { text: 'Analyse en cours...', trend: 'STABLE', trendIcon: '➡️', riskLevel: 'MODÉRÉ' };

  // Prepare chart data
  const chartData = {
    labels: history.map(h => {
      const date = new Date(h.timestamp);
      return date.toLocaleDateString('fr-FR', { month: 'short', day: 'numeric' });
    }),
    datasets: [
      {
        label: 'BTC Dominance (%)',
        data: history.map(h => h.dominance),
        borderColor: '#f7931a',
        backgroundColor: 'rgba(247, 147, 26, 0.1)',
        fill: true,
        tension: 0.4,
        pointRadius: 2,
        pointBackgroundColor: '#f7931a',
        pointBorderColor: '#fff',
        pointBorderWidth: 1,
        pointHoverRadius: 4
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: { usePointStyle: true, padding: 20, font: { size: 12 } }
      },
      title: {
        display: true,
        text: 'BTC Dominance - 1 Année d\'Historique',
        font: { size: 14, weight: 'bold' }
      }
    },
    scales: {
      y: {
        beginAtZero: false,
        min: 0,
        max: 100,
        title: { display: true, text: 'Dominance (%)' },
        grid: { color: 'rgba(0,0,0,0.05)' }
      },
      x: {
        title: { display: true, text: 'Date' },
        grid: { display: false }
      }
    }
  };

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header with back button */}
      <div style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: '8px 16px',
            borderRadius: 6,
            background: '#f0f0f0',
            border: '1px solid #ddd',
            cursor: 'pointer',
            fontSize: '0.9rem'
          }}
        >
          ← Retour
        </button>
        <h1 style={{ margin: 0, fontSize: '2rem' }}>
          {analysisData.trendIcon} Analyse Bitcoin Dominance
        </h1>
      </div>

      {/* Current Stats */}
      <div
        style={{
          background: 'linear-gradient(135deg, #f7931a 0%, #f7931aaa 100%)',
          color: '#fff',
          borderRadius: 12,
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
        }}
      >
        <div style={{ fontSize: '0.9rem', opacity: 0.9, marginBottom: '0.5rem' }}>Valeur Actuelle</div>
        <div style={{ fontSize: '3rem', fontWeight: '700', marginBottom: '0.5rem' }}>
          {data?.dominance?.toFixed(2)}%
        </div>
        <div style={{ fontSize: '0.95rem', opacity: 0.95 }}>
          Mis à jour le {new Date(data?.timestamp).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>

      {/* SECTION 1: Graph */}
      <div
        style={{
          background: '#fff',
          border: '1px solid #eee',
          borderRadius: 12,
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
        }}
      >
        <h2 style={{ marginTop: 0, marginBottom: '1.5rem', fontSize: '1.3rem', color: '#333' }}>
          📊 Section 1: Historique Complet
        </h2>
        <div style={{ height: '400px', position: 'relative', marginBottom: '1rem' }}>
          <Line data={chartData} options={chartOptions} />
        </div>
        <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '1rem' }}>
          <strong>💡 Interprétation :</strong> Ce graphique montre l'évolution de la dominance BTC sur 1 an. Une augmentation signale une
          rotation vers Bitcoin (actif défensif). Une baisse indique une "alt season" avec appétence au risque accrue.
        </div>
      </div>

      {/* SECTION 2: Auto-Analysis */}
      <div
        style={{
          background: '#fafafa',
          border: '1px solid #eee',
          borderRadius: 12,
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
        }}
      >
        <h2 style={{ marginTop: 0, marginBottom: '1.5rem', fontSize: '1.3rem', color: '#333' }}>
          🤖 Section 2: Analyse Automatique
        </h2>

        {/* Trend Badge */}
        <div
          style={{
            display: 'inline-block',
            padding: '0.5rem 1rem',
            borderRadius: 6,
            background: analysisData.trend === 'BULLISH' ? '#d4edda' : analysisData.trend === 'BEARISH' ? '#f8d7da' : '#e2e3e5',
            color: analysisData.trend === 'BULLISH' ? '#155724' : analysisData.trend === 'BEARISH' ? '#721c24' : '#383d41',
            fontWeight: 'bold',
            marginBottom: '1rem'
          }}
        >
          Tendance: {analysisData.trend}
        </div>

        {/* Analysis Text */}
        <div
          style={{
            fontSize: '0.95rem',
            lineHeight: '1.6',
            color: '#333',
            whiteSpace: 'pre-wrap',
            marginBottom: '1.5rem'
          }}
        >
          {analysisData.text}
        </div>

        {/* Key Metrics */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '1rem',
            marginTop: '1.5rem'
          }}
        >
          <div style={{
            background: '#fff',
            border: '1px solid #ddd',
            borderRadius: 8,
            padding: '1rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem' }}>Variation 1 Semaine</div>
            <div style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              color: analysisData.change1w > 0 ? '#27ae60' : analysisData.change1w < 0 ? '#e74c3c' : '#666'
            }}>
              {analysisData.change1w > 0 ? '+' : ''}{analysisData.change1w?.toFixed(2)}%
            </div>
          </div>
          <div style={{
            background: '#fff',
            border: '1px solid #ddd',
            borderRadius: 8,
            padding: '1rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem' }}>Variation 1 Mois</div>
            <div style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              color: analysisData.change1m > 0 ? '#27ae60' : analysisData.change1m < 0 ? '#e74c3c' : '#666'
            }}>
              {analysisData.change1m > 0 ? '+' : ''}{analysisData.change1m?.toFixed(2)}%
            </div>
          </div>
          <div style={{
            background: '#fff',
            border: '1px solid #ddd',
            borderRadius: 8,
            padding: '1rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem' }}>Variation 3 Mois</div>
            <div style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              color: analysisData.change3m > 0 ? '#27ae60' : analysisData.change3m < 0 ? '#e74c3c' : '#666'
            }}>
              {analysisData.change3m > 0 ? '+' : ''}{analysisData.change3m?.toFixed(2)}%
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 3: Portfolio Impact */}
      <div
        style={{
          background: portfolio ? '#fff3cd' : '#e2e3e5',
          border: portfolio ? '1px solid #ffc107' : '1px solid #ddd',
          borderRadius: 12,
          padding: '2rem',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
        }}
      >
        <h2 style={{ marginTop: 0, marginBottom: '1.5rem', fontSize: '1.3rem', color: '#333' }}>
          💼 Section 3: Impact sur Votre Portfolio
        </h2>

        {portfolio ? (
          <div>
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>Exposition Altcoin</div>
              <div
                style={{
                  fontSize: '2.5rem',
                  fontWeight: 'bold',
                  color: altcoinExposure.percentage >= 70 ? '#e74c3c' : altcoinExposure.percentage >= 50 ? '#f39c12' : '#27ae60'
                }}
              >
                {altcoinExposure.percentage}%
              </div>
              <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
                ({altcoinExposure.count} altcoins pour €{altcoinExposure.totalValue?.toLocaleString('fr-FR', { maximumFractionDigits: 2 })})
              </div>
            </div>

            <div
              style={{
                background: '#fff',
                border: '1px solid #ddd',
                borderRadius: 8,
                padding: '1rem',
                marginTop: '1.5rem'
              }}
            >
              <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: '#333' }}>📈 Évaluation du Risque</div>
              <div
                style={{
                  fontSize: '0.95rem',
                  lineHeight: '1.6',
                  color: '#333'
                }}
              >
                {altcoinExposure.percentage >= 70 ? (
                  <>
                    <strong style={{ color: '#e74c3c' }}>⚠️ Risque: ÉLEVÉ</strong>
                    <br />
                    Votre portfolio a une exposition importante aux altcoins ({altcoinExposure.percentage}%). Si la dominance BTC
                    continue d'augmenter, vos altcoins sous-performeront potentiellement, impactant négativement votre portefeuille.
                    <br />
                    <br />
                    <strong>💡 Recommandation :</strong> Envisagez de rééquilibrer vers plus de Bitcoin ou des actifs défensifs.
                  </>
                ) : altcoinExposure.percentage >= 40 ? (
                  <>
                    <strong style={{ color: '#f39c12' }}>⚖️ Risque: MODÉRÉ</strong>
                    <br />
                    Votre exposition aux altcoins est équilibrée. Une augmentation de la dominance BTC aura un impact limité sur
                    votre performance globale.
                  </>
                ) : (
                  <>
                    <strong style={{ color: '#27ae60' }}>✓ Risque: FAIBLE</strong>
                    <br />
                    Votre portfolio est bien diversifié vers Bitcoin. Vous êtes protégé en cas d'augmentation de la dominance BTC.
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: '0.95rem', color: '#666' }}>
            📭 Aucun portefeuille trouvé. Créez ou importez un portefeuille pour voir l'analyse d'impact.
          </div>
        )}
      </div>
    </div>
  );
};

export default BtcDominanceDetail;
